/**
 * Utility functions for detecting which PDA (Patient Decision Aid) is currently active
 * and providing related navigation information
 */

export interface PDAInfo {
  isHirsutism: boolean;
  isPcosDiagnosis: boolean;
  isIrregularPeriods: boolean;
  isPcosWeightManagement: boolean;
  isPcosMetabolicHealth: boolean;
  isFertility: boolean;
  pdaName: string;
  pdaLink: string;
  optionsLink: string;
  secondaryTitle?: string;
  secondaryLink?: string;
}

/**
 * Detects which PDA is currently active based on the URL path
 * Returns PDA detection flags and navigation links
 *
 * @param treatmentId - Optional treatment ID to use as fallback
 * @returns PDAInfo object with detection flags and navigation links
 */
export const detectPDA = (treatmentId?: string): PDAInfo => {
  // SSR-safe path detection
  const currentPath =
    typeof window !== "undefined" ? window.location.pathname : "";

  // Determine which PDA we're in from URL path
  const isHirsutism = currentPath.includes("hirsutism");
  const isPcosDiagnosis = currentPath.includes("pcos-diagnosis");
  const isIrregularPeriods = currentPath.includes("irregular-periods");
  const isPcosWeightManagement = currentPath.includes("pcos-weight-management");
  const isPcosMetabolicHealth = currentPath.includes("pcos-metabolic-health");
  const isFertility = currentPath.includes("/fertility");

  // Check if we're on the root page of any PDA
  const isRootPDAPage =
    (isHirsutism && currentPath === "/decision-aid/hirsutism/") ||
    (isPcosDiagnosis && currentPath === "/decision-aid/pcos-diagnosis/") ||
    (isIrregularPeriods &&
      currentPath === "/decision-aid/irregular-periods/") ||
    (isPcosWeightManagement &&
      currentPath === "/decision-aid/pcos-weight-management/") ||
    (isPcosMetabolicHealth &&
      currentPath === "/decision-aid/pcos-metabolic-health/") ||
    (isFertility && currentPath === "/decision-aid/fertility/");
  console.log("isPcosWeightManagement", isPcosWeightManagement);
  console.log("isRootPDAPage", isRootPDAPage);
  // Determine PDA name for navigation
  const pdaName = isRootPDAPage
    ? "Back to Decision Aids"
    : isHirsutism
    ? "Excess Hair (Hirsutism)"
    : isPcosDiagnosis
    ? "Pcos diagnosis"
    : isIrregularPeriods
    ? "Irregular Periods"
    : isPcosWeightManagement
    ? "Body Shape and Size"
    : isPcosMetabolicHealth
    ? "PCOS Metabolic Health"
    : isFertility
    ? "Navigating Fertility"
    : treatmentId || "Excess Hair (Hirsutism)";

  // Determine PDA link (first screen)
  // determine the first screen of each pda
  const pdaLink = isRootPDAPage
    ? "/decision-aid"
    : isHirsutism
    ? "/decision-aid/hirsutism"
    : isPcosDiagnosis
    ? "/decision-aid/pcos-diagnosis"
    : isIrregularPeriods
    ? "/decision-aid/irregular-periods"
    : isPcosWeightManagement
    ? "/decision-aid/pcos-weight-management"
    : isPcosMetabolicHealth
    ? "/decision-aid/pcos-metabolic-health"
    : isFertility
    ? "/decision-aid/fertility"
    : "/decision-aid/hirsutism";

  // Determine options overview link
  const optionsLink = isHirsutism
    ? "/decision-aid/hirsutism/options"
    : isPcosDiagnosis
    ? "/decision-aid/pcos-diagnosis/options"
    : isIrregularPeriods
    ? "/decision-aid/irregular-periods/options"
    : isPcosWeightManagement
    ? "/decision-aid/pcos-weight-management/options"
    : isPcosMetabolicHealth
    ? "/decision-aid/pcos-metabolic-health/options"
    : isFertility
    ? "/decision-aid/fertility/explore-your-options"
    : "/decision-aid/hirsutism/options";

  // Determine secondary button for specific PDAs
  let secondaryTitle: string | undefined;
  let secondaryLink: string | undefined;

  if (isPcosMetabolicHealth) {
    // Only show "Diabetes screening" button for options 1-3 (fbg-test, hba1c-test, ogtt-test)
    const isDiabetesScreeningOption =
      currentPath.includes("/fbg-test") ||
      currentPath.includes("/hba1c-test") ||
      currentPath.includes("/ogtt-test");

    if (isDiabetesScreeningOption) {
      secondaryTitle = "Diabetes screening";
      secondaryLink = "/decision-aid/pcos-metabolic-health/options/diabetes/";
    }
  }

  if (isPcosWeightManagement) {
    // Option 1: Healthy lifestyle (not focused on weight)
    // Option 2: Healthy lifestyle (focused on weight loss)
    // Option 3: Metformin
    // Option 4: Orlistat
    // Option 5: Naltrexone with Bupropion
    // Option 6: Phentermine and Topiramate
    // Option 7: Incretins-based medications
    // Option 8: Surgery to support weight loss

    // Options 1, 2, 8: "Explore your options"
    const isExploreYourOptionsOption =
      currentPath.includes("/healthy-lifestyle-not-focused-on-weight") ||
      currentPath.includes("/healthy-lifestyle-focused-on-weight") ||
      currentPath.includes("/surgery-to-support-weight-loss") ||
      currentPath.includes("/medications-to-support-weight-loss");

    // Options 3-7: "Medication options"
    const isMedicationOption =
      currentPath.includes("/metformin-wm") ||
      currentPath.includes("/orlistat") ||
      currentPath.includes("/naltrexone-with-bupropion") ||
      currentPath.includes("/phentermine-and-topiramate") ||
      currentPath.includes("/incretins-based-medication");

    if (isExploreYourOptionsOption) {
      secondaryTitle = "Explore your options";
      secondaryLink = "/decision-aid/pcos-weight-management/explore-options";
    } else if (isMedicationOption) {
      secondaryTitle = "Medication options";
      secondaryLink =
        "/decision-aid/pcos-weight-management/medications-to-support-weight-loss";
    }
  }

  if (isFertility) {
    // Option 1: Natural conception
    // Options 2.1-2.5: Ovulation induction (Metformin, Letrozole, Clomiphene, Gonadotropin, Ovarian surgery)
    // Options 3.1-3.2: Advanced Fertility treatments (IVF, IVM)

    // Options 2.1-2.5: "Ovulation induction"
    const isOvulationInductionOption =
      currentPath.includes("/metformin") ||
      currentPath.includes("/letrozole") ||
      currentPath.includes("/clomiphene") ||
      currentPath.includes("/gonadotropin") ||
      currentPath.includes("/ovarian-surgery");

    // Options 3.1-3.2: "Advanced Treatments"
    const isAdvancedTreatmentOption =
      currentPath.includes("/ivf") || currentPath.includes("/ivm");

    // Option groups: "Explore your options"
    const isExploreYourOptionsProgress =
      currentPath.includes("/natural-conception") ||
      currentPath.includes("/ovulation-induction") ||
      currentPath.includes("/advanced-fertility-treatments");

    if (isOvulationInductionOption) {
      secondaryTitle = "Ovulation induction";
      secondaryLink =
        "/decision-aid/fertility/option-groups/ovulation-induction";
    } else if (isAdvancedTreatmentOption) {
      secondaryTitle = "Advanced treatments";
      secondaryLink =
        "/decision-aid/fertility/option-groups/advanced-fertility-treatments";
    } else if (isExploreYourOptionsProgress) {
      secondaryTitle = "Explore your options";
      secondaryLink = "/decision-aid/fertility/explore-your-options";
    }
  }

  return {
    isHirsutism,
    isPcosDiagnosis,
    isIrregularPeriods,
    isPcosWeightManagement,
    isPcosMetabolicHealth,
    isFertility,
    pdaName,
    pdaLink,
    optionsLink,
    secondaryTitle,
    secondaryLink,
  };
};
